import { Select } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { FC, useCallback, useMemo } from 'react';

import styles from './flagSelect.module.scss';

export type ItemT = {
  id: string;
  flag: string;
  name: string;
};

export type ItemPropsT = {
  item: ItemT;
  disabled: boolean;
  onSelect: (country: ItemT) => void;
};

type PaginationType = {
  infinite?: boolean;
  itemCount?: number;
  nextEnable: boolean;
  onNext?: () => Promise<unknown>;
  previousEnable: boolean;
  onPrevious?: () => Promise<unknown>;
};

interface Props {
  disabled?: boolean;
  isLoading?: boolean;
  items: Array<ItemT>;
  onSelect: (item: ItemT) => void;
  onSearch?: (value: string) => void;
  pagination?: PaginationType;
  placeholder?: string;
  selectedItem?: string | null;
}

const FlagSelect: FC<Props> = ({
  disabled,
  isLoading,
  items,
  onSelect,
  onSearch,
  pagination,
  placeholder: propsPlaceholder,
  selectedItem,
}) => {
  const placeholder = isLoading ? 'Loading...' : propsPlaceholder;
  const selectedItems = selectedItem && !isLoading ? [selectedItem] : undefined;
  const selectedFlag = useMemo(
    () => items.find(({ id }) => id === selectedItem)?.flag?.toLowerCase(),
    [items, selectedItem],
  );

  const itemComponent = useCallback((props: ItemPropsT) => {
    const {
      item: { id, flag, name },
      item,
      disabled,
      onSelect,
      ...otherProps
    } = props;
    const country = flag?.toLowerCase() || '';

    return (
      <button
        {...otherProps}
        type="button"
        className={styles.item}
        onClick={() => onSelect(item)}
        key={id}
        disabled={disabled}
      >
        <span className={classnames(`flag ${country}`, styles.itemFlag)} />
        <span className={styles.itemName}>{name}</span>
      </button>
    );
  }, []);

  const prefix = {
    noWrap: true,
    type: 'custom',
    value: (
      <div className={styles.selectPrefix}>
        <span className={classnames('flag', selectedFlag)} />
      </div>
    ),
  };

  const isPrefixShown = !isLoading && !!selectedFlag;

  return (
    <Select
      className={classnames('flags', styles.select)}
      disabled={disabled || isLoading}
      itemComponent={itemComponent}
      items={items}
      onSearch={onSearch}
      onSelect={onSelect}
      pagination={pagination}
      placeholder={placeholder}
      prefix={isPrefixShown && prefix}
      searchable
      selectedItems={selectedItems}
    />
  );
};

export default FlagSelect;
